import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { LANGS, setLang, useLang, useT } from "../../i18n";
import { cn } from "../../lib/format";

/**
 * Language dropdown ("EN ▾" → Français / English), compact enough for the
 * narrowest phone headers. The choice is remembered in this browser.
 */
export default function LanguageSwitcher({ className }: { className?: string }) {
  const lang = useLang();
  const t = useT();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  // Close on outside click and Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (l: (typeof LANGS)[number]) => {
    setLang(l);
    setOpen(false);
  };

  return (
    <div ref={root} className={cn("relative shrink-0", className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t.lang.label}: ${t.lang.names[lang]}`}
        title={t.lang.label}
        className="flex h-8 cursor-pointer items-center gap-0.5 rounded border-0 bg-surface pr-1.5 pl-2 text-xs font-medium text-ink uppercase transition-colors hover:bg-surface-hover"
      >
        {lang}
        <ChevronDown size={13} aria-hidden="true" className={cn("text-muted transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t.lang.label}
          className="absolute top-full right-0 z-50 m-0 mt-1.5 w-36 list-none overflow-hidden rounded-card border border-line-strong bg-white p-1 shadow-lg"
        >
          {LANGS.map((l) => (
            <li key={l} role="option" aria-selected={lang === l}>
              <button
                type="button"
                lang={l}
                onClick={() => choose(l)}
                className={cn(
                  "flex w-full cursor-pointer items-center justify-between gap-2 rounded border-0 px-2.5 py-2 text-left text-[13px] text-ink hover:bg-surface",
                  lang === l ? "bg-surface font-medium" : "bg-transparent",
                )}
              >
                {t.lang.names[l]}
                {lang === l && <Check size={14} aria-hidden="true" className="text-brand" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
